/**
 * The Madaline legal corpus — the documents published at `/legal/:slug`
 * (§14.1), and among them the versioned disclosures a user must accept before
 * a run may start (§12.1 `disclosure_acceptances.doc_key`).
 *
 * This module is BROWSER-SAFE: it is the manifest, the short legends, the
 * operator's details (`./operator.ts`) and the eligibility rules
 * (`./eligibility.ts`), with no filesystem access. The document bodies live
 * beside it as `*.md` and are read by `@madaline/shared/legal/bodies`, which is
 * Node-only — the API serves every document from `GET /api/v1/legal/:slug`,
 * and the web bundles the markdown into the legal pages' own chunk. Both
 * render the operator's details into the text with the same function, so the
 * bytes a stranger reads and the bytes an acceptance is hashed against are
 * the same.
 *
 * It is deliberately NOT re-exported from `@madaline/shared`'s root index: the
 * root is imported by `apps/web`, and keeping the two subpaths separate is
 * what stops `node:fs` reaching a Vite bundle.
 *
 * FOUR KINDS OF DOCUMENT (`group`).
 *
 *  - The four DISCLOSURES (`LEGAL_DOCUMENTS`, keyed by `LegalDocKey`). These
 *    are the §12.1 doc keys. Acceptance is recorded per (user, doc_key,
 *    version), the run gate enforces it, and the disclosure routes list and
 *    accept exactly these four.
 *  - The two AGREEMENTS (`LEGAL_POLICIES`): the terms of service and the
 *    privacy policy. Each sign-up made through the form records the version
 *    the form showed (`disclosure_acceptances`, written by
 *    `apps/api/src/plugins/disclosures.ts` `recordAgreementRows`), and
 *    Settings can put the current version on record. They are NOT part of the
 *    run gate: a new version takes effect by notice, as the Terms say, so
 *    changing one puts nobody outside a gate.
 *  - The POLICIES that are part of the terms (`LEGAL_REFERENCES`, group
 *    `policy`): the acceptable use policy, the agent software licence and the
 *    copyright policy. The Terms incorporate them, so agreeing to the Terms is
 *    agreeing to them; the Terms' acceptance record carries each one's
 *    version and digest. Nothing asks for them separately.
 *  - The STATEMENTS (`LEGAL_REFERENCES`, group `statement`): the subprocessor
 *    list (part of the privacy policy), the security page and the
 *    accessibility statement. Published facts; nothing is accepted.
 *
 * `LEGAL_CORPUS` is all of them, in reading order, for the public pages.
 *
 * VERSIONING. A document's `version` is the date it took effect. For a
 * disclosure, BUMPING THE VERSION REQUIRES EVERY USER TO RE-ACCEPT IT before
 * their next run — that is the whole mechanism, and `outstandingDisclosures`
 * is where it is enforced. For an agreement, a new version is announced in
 * Madaline (the agreements notice) and can be put on record from it or from
 * Settings; it blocks nothing. Change a document's text only together with
 * its version: the API records the sha256 of the RENDERED body beside every
 * acceptance, so text that changes under an unchanged version breaks the
 * record of what a user accepted.
 *
 * REVIEW. Every document carries `reviewed`. While it is false the public page
 * says, plainly, that the document is a draft pending legal review. Set it to
 * true document by document, as counsel signs each one off (spec §18 open
 * question 2c) — and only once its text has no decision left open. Flipping it
 * changes no text and no version, so it puts nobody outside the gate.
 */
export * from "./legends.js";
export * from "./operator.js";
export * from "./eligibility.js";

/** §12.1: `risk_disclosure | software_not_advice | hypothetical_performance | venue_terms_ack`. */
export const LEGAL_DOC_KEYS = [
  "risk_disclosure",
  "software_not_advice",
  "hypothetical_performance",
  "venue_terms_ack",
] as const;

export type LegalDocKey = (typeof LEGAL_DOC_KEYS)[number];

/** The two agreements a sign-up records. Not §12.1 doc keys: they gate no run. */
export const LEGAL_POLICY_KEYS = ["terms_of_service", "privacy_policy"] as const;

export type LegalPolicyKey = (typeof LEGAL_POLICY_KEYS)[number];

/**
 * The policies the Terms incorporate, and the published statements. No
 * acceptance of these is recorded on its own: the Terms' acceptance carries
 * each incorporated policy's version and digest, and a statement is accepted
 * by nobody.
 */
export const LEGAL_REFERENCE_KEYS = [
  "acceptable_use_policy",
  "agent_software_licence",
  "copyright_policy",
  "subprocessor_list",
  "security_statement",
  "accessibility_statement",
] as const;

export type LegalReferenceKey = (typeof LEGAL_REFERENCE_KEYS)[number];

/** Any document in the corpus. */
export type LegalCorpusKey = LegalDocKey | LegalPolicyKey | LegalReferenceKey;

/** Which shelf of `/legal` a document sits on. */
export type LegalGroup = "agreement" | "policy" | "disclosure" | "statement";

export interface LegalDocument<K extends LegalCorpusKey = LegalDocKey> {
  /** For a disclosure, the `disclosure_acceptances.doc_key` value. */
  readonly key: K;
  /** The `/legal/:doc` slug (§14.1) and the markdown file's basename. */
  readonly slug: string;
  readonly title: string;
  /** ISO date. For a disclosure, bumping this invalidates every existing acceptance. */
  readonly version: string;
  readonly effectiveDate: string;
  /** One line for the acceptance list and the index. Descriptive, never persuasive. */
  readonly summary: string;
  /** Whether a user must accept it before a run may start. */
  readonly acceptanceRequired: boolean;
  /** Which shelf it sits on. */
  readonly group: LegalGroup;
  /**
   * Whether counsel has reviewed this text (spec §18 open question 2c).
   *
   * Defaults to false and stays false until a lawyer has read THIS version.
   * While it is false, `/legal/:slug` renders a notice that the document is a
   * draft pending legal review — the notice disappears document by document
   * as each is signed off, with no change to the text or the version.
   */
  readonly reviewed: boolean;
}

/**
 * The disclosures' version — 2026-10-15, when the review before counsel found
 * statements in them that were not true, and corrected them: the hypothetical
 * performance legend, labelled as 17 CFR 4.41(b)(1)(i), was not the text the
 * CFR prints, and was said to sit beside every paper result
 * (the paper screens carry a shorter notice); the venue terms said Madaline
 * computes no trading result (the daily loss limit computes the day's loss),
 * described a switch for a funds-transfer permission that does not exist, and
 * misdescribed how a key of unknown permissions is treated; the risk
 * disclosure said Madaline does nothing once a strategy stops (it cancels the
 * orders a run left resting). The bodies had been rewritten in full on
 * 2026-10-13. Every account that accepted 2026-10-13 owes these four again
 * before its next run — the mechanism, not a side effect.
 */
const DISCLOSURE_VERSION = "2026-10-15";

/**
 * The agreements' version — 2026-10-15, when the Terms of service and the
 * Privacy policy were rewritten against the product and the deployment (one
 * server, in Canada; eligibility and sanctions; the subscription; every
 * cookie and stored item named). The first version, 2026-10-13, described
 * servers that do not exist. Every account with 2026-10-13 on record is told
 * in Madaline that a new version is in force, and can put it on record from
 * that notice or from Settings → Disclosures. Nothing is blocked meanwhile:
 * the Terms say a new version takes effect by notice.
 */
const AGREEMENT_VERSION = "2026-10-15";

/** The policies the Terms incorporate, and the statements, first published 2026-10-15. */
const REFERENCE_VERSION = "2026-10-15";

export const LEGAL_DOCUMENTS: readonly LegalDocument[] = [
  {
    key: "risk_disclosure",
    slug: "risk-disclosure",
    title: "Risk disclosure",
    version: DISCLOSURE_VERSION,
    effectiveDate: DISCLOSURE_VERSION,
    summary:
      "How you can lose money trading event contracts, including the entire amount you put at risk.",
    acceptanceRequired: true,
    group: "disclosure",
    reviewed: false,
  },
  {
    key: "software_not_advice",
    slug: "software-not-advice",
    title: "Software, not advice",
    version: DISCLOSURE_VERSION,
    effectiveDate: DISCLOSURE_VERSION,
    summary:
      "Madaline is software you operate. It is not an adviser or a broker, and it does not direct your account.",
    acceptanceRequired: true,
    group: "disclosure",
    reviewed: false,
  },
  {
    key: "hypothetical_performance",
    slug: "hypothetical-performance",
    title: "Hypothetical performance",
    version: DISCLOSURE_VERSION,
    effectiveDate: DISCLOSURE_VERSION,
    summary:
      "Why every result made without a real order is hypothetical, and exactly how it can differ from trading at a venue.",
    acceptanceRequired: true,
    group: "disclosure",
    reviewed: false,
  },
  {
    key: "venue_terms_ack",
    slug: "venue-terms",
    title: "Venue terms",
    version: DISCLOSURE_VERSION,
    effectiveDate: DISCLOSURE_VERSION,
    summary:
      "Your account is governed by your venue's own agreement with you; Madaline is not a party to it.",
    acceptanceRequired: true,
    group: "disclosure",
    reviewed: false,
  },
];

export const LEGAL_POLICIES: readonly LegalDocument<LegalPolicyKey>[] = [
  {
    key: "terms_of_service",
    slug: "terms",
    title: "Terms of service",
    version: AGREEMENT_VERSION,
    effectiveDate: AGREEMENT_VERSION,
    summary:
      "The agreement between you and Madaline's operator: what the service is, who may use it, the subscription, and where responsibility lies.",
    acceptanceRequired: false,
    group: "agreement",
    reviewed: false,
  },
  {
    key: "privacy_policy",
    slug: "privacy",
    title: "Privacy policy",
    version: AGREEMENT_VERSION,
    effectiveDate: AGREEMENT_VERSION,
    summary:
      "What Madaline stores about you, who it is sent to, where it is held, how long it is kept, and how to get a copy or delete it.",
    acceptanceRequired: false,
    group: "agreement",
    reviewed: false,
  },
];

export const LEGAL_REFERENCES: readonly LegalDocument<LegalReferenceKey>[] = [
  {
    key: "acceptable_use_policy",
    slug: "acceptable-use",
    title: "Acceptable use policy",
    version: REFERENCE_VERSION,
    effectiveDate: REFERENCE_VERSION,
    summary:
      "What you may not do with Madaline: no market manipulation, no getting around a limit or a venue's rules, no attacks on any service.",
    acceptanceRequired: false,
    group: "policy",
    reviewed: false,
  },
  {
    key: "agent_software_licence",
    slug: "agent-licence",
    title: "Agent software licence",
    version: REFERENCE_VERSION,
    effectiveDate: REFERENCE_VERSION,
    summary:
      "The licence for the Madaline agent: what it does on your computer, what it sends us, and what it never sends. Not offered yet.",
    acceptanceRequired: false,
    group: "policy",
    reviewed: false,
  },
  {
    key: "copyright_policy",
    slug: "copyright",
    title: "Copyright policy",
    version: REFERENCE_VERSION,
    effectiveDate: REFERENCE_VERSION,
    summary:
      "How to report material in Madaline that infringes copyright, how to respond to a report, and what happens to repeat infringers.",
    acceptanceRequired: false,
    group: "policy",
    reviewed: false,
  },
  {
    key: "subprocessor_list",
    slug: "subprocessors",
    title: "Subprocessors",
    version: REFERENCE_VERSION,
    effectiveDate: REFERENCE_VERSION,
    summary:
      "Every company that handles personal information for Madaline, what it receives, where, and whether it is switched on today.",
    acceptanceRequired: false,
    group: "statement",
    reviewed: false,
  },
  {
    key: "security_statement",
    slug: "security",
    title: "Security",
    version: REFERENCE_VERSION,
    effectiveDate: REFERENCE_VERSION,
    summary:
      "How to report a security problem in Madaline, what is in scope, what we do with a report, and how your account is protected.",
    acceptanceRequired: false,
    group: "statement",
    reviewed: false,
  },
  {
    key: "accessibility_statement",
    slug: "accessibility",
    title: "Accessibility statement",
    version: REFERENCE_VERSION,
    effectiveDate: REFERENCE_VERSION,
    summary:
      "The standard Madaline is built to, how it is checked, what is known not to meet it, and how to tell us about a barrier.",
    acceptanceRequired: false,
    group: "statement",
    reviewed: false,
  },
];

/**
 * Everything, in the order a reader meets it: the two agreements, the
 * policies the Terms incorporate, the four disclosures, then the statements.
 */
export const LEGAL_CORPUS: readonly LegalDocument<LegalCorpusKey>[] = [
  ...LEGAL_POLICIES,
  ...LEGAL_REFERENCES.filter((d) => d.group === "policy"),
  ...LEGAL_DOCUMENTS,
  ...LEGAL_REFERENCES.filter((d) => d.group === "statement"),
];

/**
 * The documents the Terms of service incorporate by reference — what agreeing
 * to the Terms also agrees to. The Terms' acceptance record carries each
 * one's version and digest beside its own.
 */
export const INCORPORATED_INTO_TERMS: readonly LegalReferenceKey[] = LEGAL_REFERENCES.filter(
  (d) => d.group === "policy",
).map((d) => d.key);

/** The keys a run gate must check, in manifest order. */
export const REQUIRED_LEGAL_DOC_KEYS: readonly LegalDocKey[] = LEGAL_DOCUMENTS.filter(
  (d) => d.acceptanceRequired,
).map((d) => d.key);

/** `{doc_key: version}` for the versions in force right now. */
export const CURRENT_LEGAL_VERSIONS: Readonly<Record<LegalDocKey, string>> = Object.freeze(
  Object.fromEntries(LEGAL_DOCUMENTS.map((d) => [d.key, d.version])) as Record<
    LegalDocKey,
    string
  >,
);

export const isLegalDocKey = (value: unknown): value is LegalDocKey =>
  typeof value === "string" && (LEGAL_DOC_KEYS as readonly string[]).includes(value);

/** A disclosure by its §12.1 key. Only the four; see `legalCorpusByKey` for all. */
export const legalDocumentByKey = (key: string): LegalDocument | undefined =>
  LEGAL_DOCUMENTS.find((d) => d.key === key);

/** A disclosure by its slug. Only the four. */
export const legalDocumentBySlug = (slug: string): LegalDocument | undefined =>
  LEGAL_DOCUMENTS.find((d) => d.slug === slug);

/** Any document, by key. */
export const legalCorpusByKey = (key: string): LegalDocument<LegalCorpusKey> | undefined =>
  LEGAL_CORPUS.find((d) => d.key === key);

/** Any document, by slug. */
export const legalCorpusBySlug = (slug: string): LegalDocument<LegalCorpusKey> | undefined =>
  LEGAL_CORPUS.find((d) => d.slug === slug);

/** What a user has on file, in the shape `disclosure_acceptances` returns. */
export interface DisclosureAcceptance {
  readonly doc_key: string;
  readonly version: string;
  readonly accepted_at?: string | Date | null;
}

/**
 * The documents a user still owes, given what they have accepted.
 *
 * An acceptance counts only when its `version` is the CURRENT one — an
 * acceptance of a superseded version is exactly the same as no acceptance,
 * which is how re-versioning forces re-acceptance.
 */
export function outstandingDisclosures(
  accepted: readonly DisclosureAcceptance[],
  documents: readonly LegalDocument[] = LEGAL_DOCUMENTS,
): LegalDocument[] {
  return documents
    .filter((d) => d.acceptanceRequired)
    .filter((d) => !accepted.some((a) => a.doc_key === d.key && a.version === d.version));
}

/** The acceptance on file for a document, current or not. */
export function acceptedVersionOf(
  accepted: readonly DisclosureAcceptance[],
  key: LegalDocKey,
): DisclosureAcceptance | undefined {
  return accepted
    .filter((a) => a.doc_key === key)
    .reduce<DisclosureAcceptance | undefined>(
      (best, a) => (best === undefined || a.version > best.version ? a : best),
      undefined,
    );
}
